// GDPR / ePrivacy consent store.
//
// Design rules this file enforces:
//  * Nothing but strictly necessary storage is used until the visitor opts in.
//  * Every non-essential category defaults to OFF (no pre-ticked boxes).
//  * Refusing is as easy as accepting, and the choice can be changed any time.
//  * The record expires so consent is re-requested rather than assumed forever
//    (6 months, in line with Italian Garante / EDPB guidance).

export type ConsentCategory = 'necessary' | 'preferences' | 'analytics' | 'marketing';

export type ConsentChoices = Record<ConsentCategory, boolean>;

export type ConsentMethod = 'accept_all' | 'reject_all' | 'custom';

export interface ConsentRecord {
  version: number;
  savedAt: string;
  method: ConsentMethod;
  choices: ConsentChoices;
}

export const CONSENT_VERSION = 1;
export const CONSENT_TTL_DAYS = 180;
const STORAGE_KEY = 'parnasa:cookie-consent';

export const CONSENT_EVENT = 'parnasa:consent-change';
export const OPEN_SETTINGS_EVENT = 'parnasa:open-cookie-settings';

export const DEFAULT_CHOICES: ConsentChoices = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

export const ALL_ACCEPTED: ConsentChoices = {
  necessary: true,
  preferences: true,
  analytics: true,
  marketing: true,
};

export interface CategoryMeta {
  id: ConsentCategory;
  title: string;
  summary: string;
  locked?: boolean;
  examples: string;
}

export const CATEGORIES: CategoryMeta[] = [
  {
    id: 'necessary',
    title: 'Strictly necessary',
    summary:
      'Required for the site to load, stay secure and remember this very cookie choice. These cannot be switched off and rely on an exemption from consent.',
    locked: true,
    examples: 'Consent record, security and load-balancing, basic accessibility preferences.',
  },
  {
    id: 'preferences',
    title: 'Preferences',
    summary:
      'Remember choices such as language or region so you do not have to set them again on your next visit.',
    examples: 'Language selection, interface preferences.',
  },
  {
    id: 'analytics',
    title: 'Analytics',
    summary:
      'Help us understand, in aggregate, which pages are useful so we can improve the site. Set only with your consent.',
    examples: 'Page views, referrers, aggregated session statistics.',
  },
  {
    id: 'marketing',
    title: 'Marketing',
    summary:
      'Would allow measuring campaigns or showing you relevant messages elsewhere. We do not use these today; the switch stays here so the choice is yours if that ever changes.',
    examples: 'Campaign attribution, audience measurement.',
  },
];

/**
 * True when the browser is broadcasting an opt-out: Global Privacy Control or
 * legacy Do Not Track. We treat either as a refusal of every optional category,
 * so no non-essential storage is used and the visitor is not nagged. An explicit
 * choice made in the preference panel still takes precedence over the signal.
 */
export function browserOptOutSignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean; msDoNotTrack?: string };
  if (nav.globalPrivacyControl === true) return true;
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack ?? (window as unknown as { doNotTrack?: string }).doNotTrack;
  return dnt === '1' || dnt === 'yes';
}

function isExpired(record: ConsentRecord): boolean {
  const savedAt = new Date(record.savedAt).getTime();
  if (Number.isNaN(savedAt)) return true;
  const ageDays = (Date.now() - savedAt) / 86_400_000;
  return ageDays > CONSENT_TTL_DAYS;
}

export function loadConsent(): ConsentRecord | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (isExpired(parsed)) return null;
    return { ...parsed, choices: { ...DEFAULT_CHOICES, ...parsed.choices, necessary: true } };
  } catch {
    // Private browsing or storage disabled: treat as "no consent given".
    return null;
  }
}

export function saveConsent(choices: ConsentChoices, method: ConsentMethod): ConsentRecord {
  const record: ConsentRecord = {
    version: CONSENT_VERSION,
    savedAt: new Date().toISOString(),
    method,
    choices: { ...choices, necessary: true },
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Nothing we can do if storage is blocked; the banner simply reappears.
  }
  window.dispatchEvent(new CustomEvent<ConsentRecord>(CONSENT_EVENT, { detail: record }));
  return record;
}

export function clearConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
}

/** Gate optional scripts on a category: `if (hasConsent('analytics')) { ... }` */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'necessary') return true;
  // No stored decision means no consent, which also covers the case where a
  // browser opt-out signal stopped us from ever asking.
  return loadConsent()?.choices[category] === true;
}

export function formatConsentDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
